/** Pure validation rules shared by every form. Messages are shown to visitors verbatim. */
import { FREE_EMAIL_DOMAINS } from '../../config/free-email-domains';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export interface FieldRules {
  readonly kind: 'text' | 'email' | 'select' | 'checkbox';
  readonly label: string;
  readonly required: boolean;
  /** Reject consumer mailbox domains. */
  readonly workEmail?: boolean;
  readonly minLength?: number;
  /** 0 or negative = no limit. */
  readonly maxLength?: number;
  readonly checked?: boolean;
}

export const isFreeEmailDomain = (email: string): boolean =>
  FREE_EMAIL_DOMAINS.includes((email.split('@').pop() ?? '').toLowerCase());

/** The first problem with a field value, or an empty string when it is valid. */
export function validateValue(rawValue: string, rules: FieldRules): string {
  const value = rawValue.trim();
  const label = rules.label.toLowerCase();
  if (rules.kind === 'checkbox') {
    return rules.required && !rules.checked ? 'Please agree so we can respond to your request.' : '';
  }
  if (rules.required && !value) return rules.kind === 'select' ? `Select your ${label}.` : `Enter your ${label}.`;
  if (!value) return '';
  if (rules.kind === 'email' && !EMAIL_PATTERN.test(value))
    return 'Enter a valid email address, like name@company.com.';
  if (rules.workEmail && isFreeEmailDomain(value)) return 'Please use your work email address.';
  if (rules.minLength && value.length < rules.minLength) return `Please enter at least ${rules.minLength} characters.`;
  if (rules.maxLength && rules.maxLength > 0 && value.length > rules.maxLength) {
    return `Please keep this under ${rules.maxLength} characters.`;
  }
  return '';
}

export const validateGroupSelection = (selectedCount: number): string =>
  selectedCount > 0 ? '' : 'Choose at least one option.';
